import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../../components/Common";
import {
  BedDouble, Pill, FileText, ArrowLeft, Plus, Lock, LogOut, ArrowRightLeft,
} from "lucide-react";
import { AddMedicationDialog, WardRoundEditor, AddendumBox, StatusChip, STATUSES } from "./parts";
import { TransferDialog } from "./TransferDialog";
import { VitalsTimeline } from "./VitalsTimeline";
import { AdmissionTimeline } from "./AdmissionTimeline";
import { DischargeDialog } from "./DischargeDialog";
import { DischargeSummaryView } from "./DischargeSummaryView";
import { HandoverBanner } from "./HandoverDialog";
import { DoctorAvailabilityDot } from "./DoctorAvailabilityDot";

export function AdmissionDetail({ admissionId, onBack, onChanged }) {
  const { user } = useAuth();
  const isDoctor = ["doctor", "hospital_admin", "super_admin"].includes(user?.role);
  const [adm, setAdm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMed, setShowAddMed] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showDischarge, setShowDischarge] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admissions/${admissionId}`); setAdm(data); }
    catch { toast.error("Failed to load admission"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [admissionId]); // eslint-disable-line

  const setStatus = async (value) => {
    try {
      setStatusSaving(true);
      await api.post(`/admissions/${admissionId}/clinical-status`, { clinical_status: value });
      toast.success("Status updated");
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update status"); }
    finally { setStatusSaving(false); }
  };

  const discontinueMed = async (medId) => {
    if (!window.confirm("Discontinue this medication?")) return;
    try {
      await api.post(`/admissions/${admissionId}/medications/${medId}/discontinue`);
      toast.success("Medication discontinued");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to discontinue"); }
  };

  if (loading) return <div className="p-6 text-slate-500 text-sm" data-testid="adm-detail-loading">Loading admission…</div>;
  if (!adm) return <div className="p-6"><EmptyState icon={BedDouble} title="Admission not found" /></div>;

  const p = adm.patient || {};
  const meds = adm.medications || [];
  const rounds = adm.ward_rounds || [];
  const active = meds.filter((m) => m.status === "Active");
  const stopped = meds.filter((m) => m.status === "Discontinued");
  const isDischarged = adm.status === "Discharged";
  const canDischarge = isDoctor && !isDischarged;
  const canTransfer = isDoctor && !isDischarged;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admission-detail">
      <div className="flex items-center justify-between ths-print-hide">
        <button onClick={onBack} data-testid="adm-back" className="text-sm text-slate-600 hover:text-slate-800 flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 tabular">{adm.admission_no}</div>
          {canTransfer && (
            <button data-testid="adm-transfer-btn" onClick={() => setShowTransfer(true)} className="h-9 px-3 rounded-lg text-sm font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><ArrowRightLeft className="w-4 h-4" /> Transfer</button>
          )}
          {canDischarge && (
            <button data-testid="adm-discharge-btn" onClick={() => setShowDischarge(true)} className="h-9 px-3 rounded-lg text-sm font-medium bg-[#B71C1C] text-white hover:bg-[#8f1414] flex items-center gap-1.5"><LogOut className="w-4 h-4" /> Discharge</button>
          )}
          {isDischarged && (
            <span className="chip chip-slate flex items-center gap-1 !inline-flex" data-testid="adm-discharged-chip"><Lock className="w-3 h-3" /> Discharged</span>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="rounded-xl bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-widest">Admitted patient</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1" data-testid="adm-patient-name">{adm.patient_name}</div>
            <div className="text-sm text-slate-500 mt-0.5 tabular">{adm.patient_ths_id} · {p.sex || "—"} · {p.age || "—"} yrs</div>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <div><div className="text-[11px] text-slate-500 uppercase">Ward / Bed</div><div className="text-sm font-medium">{adm.ward} {adm.bed_no ? `· Bed ${adm.bed_no}` : ""}</div></div>
            <div><div className="text-[11px] text-slate-500 uppercase">Admitted by</div><div className="text-sm font-medium flex items-center gap-1.5"><DoctorAvailabilityDot name={adm.admitting_doctor || adm.admitted_by_name} />{adm.admitting_doctor || adm.admitted_by_name}</div></div>
            <div><div className="text-[11px] text-slate-500 uppercase">Admitted at</div><div className="text-sm font-medium">{new Date(adm.admitted_at).toLocaleString()}</div></div>
            <div><div className="text-[11px] text-slate-500 uppercase">Status</div><div className="mt-0.5" data-testid="adm-clinical-status"><StatusChip value={adm.clinical_status || "Not set"} /></div></div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><div className="text-[11px] text-slate-500 uppercase">Admitting diagnosis</div><div className="text-sm text-slate-800 mt-1">{adm.admitting_diagnosis}</div></div>
          <div><div className="text-[11px] text-slate-500 uppercase">Reason</div><div className="text-sm text-slate-800 mt-1">{adm.reason}</div></div>
        </div>
        {isDoctor && !isDischarged && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-500 uppercase">Update clinical status:</span>
            {STATUSES.map((s) => (
              <button key={s}
                data-testid={`set-status-${s.replace(/\s+/g,'-').toLowerCase()}`}
                disabled={statusSaving || adm.clinical_status === s}
                onClick={() => setStatus(s)}
                className={`h-7 px-2.5 rounded-full text-[11px] font-medium border transition ${adm.clinical_status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"} disabled:opacity-60`}
              >{s}</button>
            ))}
          </div>
        )}
        {(adm.transfers || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-[11px] text-slate-500 uppercase mb-1">Ward transfer history</div>
            <ul className="text-xs text-slate-600 space-y-1" data-testid="adm-transfer-history">
              {adm.transfers.slice().reverse().slice(0, 3).map((t) => (
                <li key={`${t.at}-${t.to_ward}`}>{new Date(t.at).toLocaleString()} · <span className="font-medium">{t.from_ward}</span> → <span className="font-medium">{t.to_ward}{t.bed_no ? ` · Bed ${t.bed_no}` : ""}</span> · by {t.by}{t.reason ? ` — ${t.reason}` : ""}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Vitals last 24h */}
      <HandoverBanner patientId={adm.patient_id} />
      <VitalsTimeline patientId={adm.patient_id} />

      {/* Admission timeline (merged event feed) */}
      <AdmissionTimeline admission={adm} />

      {/* Discharge summary (once discharged) */}
      {isDischarged && (
        <DischargeSummaryView admission={adm} canAddendum={isDoctor} onAddendum={load} />
      )}

      {/* Medications */}
      <SectionCard
        title="Current medications"
        testId="adm-meds-card"
        actions={isDoctor && !isDischarged && (
          <button data-testid="adm-add-med" onClick={() => setShowAddMed(true)} className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add medication
          </button>
        )}
      >
        {active.length === 0 ? (
          <EmptyState icon={Pill} title="No active medications" description={isDoctor ? "Add the first inpatient medication for this admission." : "Only doctors can prescribe inpatient medications."} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {active.map((m) => (
              <li key={m.med_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`med-row-${m.med_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800">{m.drug} · {m.dose} · {m.route}</div>
                  <div className="text-xs text-slate-500">{m.frequency}{m.notes ? ` · ${m.notes}` : ""} · started {new Date(m.started_at).toLocaleString()} · by {m.prescribed_by_name}</div>
                </div>
                {isDoctor && !isDischarged && (
                  <button data-testid={`med-discontinue-${m.med_id}`} onClick={() => discontinueMed(m.med_id)} className="h-8 px-3 rounded-lg text-xs font-medium border border-red-200 text-[#B71C1C] hover:bg-[#fdecec]">Discontinue</button>
                )}
              </li>
            ))}
          </ul>
        )}
        {stopped.length > 0 && (
          <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-3">
            <div className="text-[11px] text-slate-500 uppercase mb-2">Discontinued ({stopped.length})</div>
            <ul className="space-y-1">
              {stopped.map((m) => (
                <li key={m.med_id} className="text-xs text-slate-500 line-through">{m.drug} · {m.dose} · stopped {m.discontinued_at ? new Date(m.discontinued_at).toLocaleDateString() : ""} by {m.discontinued_by}</li>
              ))}
            </ul>
          </div>
        )}
      </SectionCard>

      {/* Ward round notes */}
      <SectionCard
        title="Ward round notes"
        testId="adm-rounds-card"
        actions={isDoctor && !isDischarged && !showEditor && (
          <button data-testid="adm-add-round" onClick={() => setShowEditor(true)} className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New ward round
          </button>
        )}
      >
        <div className="p-5 space-y-4">
          {isDoctor && showEditor && !isDischarged && (
            <WardRoundEditor admissionId={admissionId} onCancel={() => setShowEditor(false)} onSaved={() => { setShowEditor(false); load(); }} />
          )}
          {rounds.length === 0 && !showEditor ? (
            <EmptyState icon={FileText} title="No ward round notes yet" description={isDoctor ? "Sign your first ward round to start the record." : "Only doctors can add ward round notes."} />
          ) : (
            <ul className="space-y-4">
              {[...rounds].reverse().map((r) => (
                <li key={r.round_id} className="rounded-xl border border-slate-200 bg-white" data-testid={`round-${r.round_id}`}>
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{r.doctor_name}</span>
                      <span className="text-xs text-slate-500">{new Date(r.at).toLocaleString()}</span>
                    </div>
                    <div>
                      {r.signed ? (
                        <span className="chip chip-green flex items-center gap-1 !inline-flex"><Lock className="w-3 h-3" /> Signed · locked</span>
                      ) : (
                        <span className="chip chip-amber">Draft</span>
                      )}
                    </div>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {r.subjective && <div><div className="text-[11px] text-slate-500 uppercase">Subjective</div><div className="mt-1 whitespace-pre-line text-slate-800">{r.subjective}</div></div>}
                    {r.objective && <div><div className="text-[11px] text-slate-500 uppercase">Objective</div><div className="mt-1 whitespace-pre-line text-slate-800">{r.objective}</div></div>}
                    {r.assessment && <div><div className="text-[11px] text-slate-500 uppercase">Assessment</div><div className="mt-1 whitespace-pre-line text-slate-800">{r.assessment}</div></div>}
                    {r.plan && <div><div className="text-[11px] text-slate-500 uppercase">Plan</div><div className="mt-1 whitespace-pre-line text-slate-800">{r.plan}</div></div>}
                  </div>
                  {(r.addenda || []).length > 0 && (
                    <div className="px-4 pb-3">
                      <div className="text-[11px] text-slate-500 uppercase mb-1">Addenda</div>
                      <ul className="space-y-2">
                        {r.addenda.map((a, i) => (
                          <li key={i} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-sm text-slate-700">
                            <div className="whitespace-pre-line">{a.text}</div>
                            <div className="text-[11px] text-slate-500 mt-1">— {a.by} · {new Date(a.at).toLocaleString()}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.signed && isDoctor && !isDischarged && (
                    <div className="px-4 pb-4">
                      <AddendumBox admissionId={admissionId} roundId={r.round_id} onSaved={load} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </SectionCard>

      {showAddMed && <AddMedicationDialog admissionId={admissionId} onClose={() => setShowAddMed(false)} onSaved={() => { setShowAddMed(false); load(); }} />}
      {showDischarge && <DischargeDialog admission={adm} onClose={() => setShowDischarge(false)} onDone={() => { setShowDischarge(false); load(); onChanged?.(); }} />}
      {showTransfer && <TransferDialog admission={adm} onClose={() => setShowTransfer(false)} onDone={() => { setShowTransfer(false); load(); onChanged?.(); }} />}
    </div>
  );
}
